/**
 * Client-side AI Enrichment Wrapper
 * 
 * This module provides a client-side interface for AI enrichment by calling
 * our server-side API endpoint instead of calling OpenAI directly.
 */

import { EnrichmentRequest, EnrichmentResponse } from '../types/enrichment';

export async function enrichCampData(
  request: EnrichmentRequest, 
  onProgress?: (message: string) => void
): Promise<EnrichmentResponse & { bulletList?: string }> {
  const startTime = Date.now();

  try {
    // Show initial progress
    onProgress?.(`🚀 Starting AI enrichment for "${request.campName}"`);
    onProgress?.(`🌐 Website: ${request.website || 'No website provided'}`);
    
    if (request.city || request.state) {
      onProgress?.(`📍 Location: ${request.city || 'Unknown'}, ${request.state || 'Unknown'}`);
    }
    
    onProgress?.(`📡 Connecting to enrichment server...`);
    
    // Progress messages to show during the wait (simulated progress)
    const progressSteps = [
      { delay: 2000, message: `🔍 Scraping website content...` },
      { delay: 5000, message: `📄 Extracting HTML and text from pages...` },
      { delay: 8000, message: `🔗 Analyzing page structure and links...` },
      { delay: 12000, message: `📱 Scanning for social media links...` },
      { delay: 18000, message: `🤖 AI is analyzing content and extracting camp details...` },
      { delay: 25000, message: `📍 Extracting address and location information...` },
      { delay: 32000, message: `🗺️ Geocoding address for map coordinates...` },
      { delay: 40000, message: `📋 Parsing activities, amenities, and dates...` },
      { delay: 48000, message: `⚙️ Validating and processing extracted data...` },
      { delay: 55000, message: `🔄 Finalizing enrichment results...` },
      { delay: 65000, message: `⏳ Still processing... This can take up to 90 seconds for complex sites...` },
    ];
    
    // Start showing progress messages at intervals
    const progressIntervals: number[] = [];
    progressSteps.forEach((step) => {
      const timeout = window.setTimeout(() => {
        onProgress?.(step.message);
      }, step.delay);
      progressIntervals.push(timeout);
    });

    // Make the API call - this is where the actual processing happens
    let response: Response;
    try {
      // Log request details for debugging
      console.log('📤 [Client] Sending enrichment request:', {
        campName: request.campName,
        hasCampPagesData: !!request.campPagesData,
        mainPageContentLength: request.campPagesData?.mainPageContent?.length || 0,
        firstChars: request.campPagesData?.mainPageContent?.substring(0, 200) || 'N/A'
      });
      
      response = await fetch('/api/ai-enrichment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ request }),
      });
    } finally {
      // Clear any remaining progress intervals when the request completes
      progressIntervals.forEach(interval => window.clearTimeout(interval));
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API call failed: ${response.status}`);
    }

    onProgress?.(`✅ Server processing complete, receiving results...`);

    const result = await response.json();
    
    if (!result.success) {
      // Check if this should be skipped (insufficient content, non-camp, etc.)
      if (result.should_skip || result.validation_result === 'INSUFFICIENT_CONTENT' || result.validation_result === 'NOT_CAMP_KEYWORDS') {
        // Create a special error that indicates this should be skipped entirely
        const skipError = new Error(result.error || 'Camp skipped to prevent inaccurate information');
        (skipError as any).shouldSkip = true;
        (skipError as any).validationResult = result.validation_result;
        (skipError as any).skipReason = result.validation_result === 'INSUFFICIENT_CONTENT' ? 'insufficient_content' : 'not_a_camp';
        throw skipError;
      }
      
      // Check for other validation failures (non-camp business)
      if (result.validation_result && result.validation_result !== 'SUMMER_CAMP') {
        // Create a special error that indicates this should be skipped entirely
        const skipError = new Error(result.error || 'Content does not appear to be a summer camp');
        (skipError as any).shouldSkip = true;
        (skipError as any).validationResult = result.validation_result;
        throw skipError;
      }
      throw new Error(result.error || 'Enrichment failed');
    }

    const enrichmentData = result.data;
    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);

    // Show server-side insights
    const metadata = enrichmentData.metadata || {};
    onProgress?.(`[${new Date().toLocaleTimeString()}] ✅ Enrichment completed successfully!`);
    onProgress?.(`[${new Date().toLocaleTimeString()}] ⏱️  Total duration: ${duration}s`);
    onProgress?.(`[${new Date().toLocaleTimeString()}] 🤖 AI Model: ${metadata.model || 'GPT-4o'}`);
    onProgress?.(`[${new Date().toLocaleTimeString()}] 🎯 Confidence Score: ${metadata.confidence_score || 'N/A'}/10`);
    
    if (metadata.tokensUsed) {
      onProgress?.(`[${new Date().toLocaleTimeString()}] 💰 Tokens Used: ${metadata.tokensUsed} (~$${(metadata.cost || 0).toFixed(4)})`);
    }

    // Show what was enriched with more details
    if (enrichmentData.description) {
      const preview = enrichmentData.description.substring(0, 100) + (enrichmentData.description.length > 100 ? '...' : '');
      onProgress?.(`[${new Date().toLocaleTimeString()}] 📝 Generated description: "${preview}"`);
    }

    // Show server processing insights if available
    if (metadata.server_insights) {
      onProgress?.(`[${new Date().toLocaleTimeString()}] 🖥️  Server processed ${metadata.server_insights.content_length?.toLocaleString() || 0} chars of content`);
      onProgress?.(`[${new Date().toLocaleTimeString()}] 🔄 Processing steps: ${metadata.server_insights.processing_steps?.length || 0} completed`);
    }

    // Show validation insights
    if (metadata.is_low_confidence) {
      onProgress?.(`[${new Date().toLocaleTimeString()}] ⚠️  Low confidence result - manual review recommended`);
    } else {
      onProgress?.(`[${new Date().toLocaleTimeString()}] ✨ High confidence result - ready for import`);
    }

    return {
      ...enrichmentData,
      metadata: {
        ...enrichmentData.metadata,
        duration,
        timestamp: new Date().toISOString()
      }
    };

  } catch (error) {
    console.error('AI enrichment API call failed:', error);
    onProgress?.(`[${new Date().toLocaleTimeString()}] ❌ AI enrichment failed: ${error.message}`);
    throw error;
  }
}

// Re-export the generateEnrichmentPrompt function from the original module
export { generateEnrichmentPrompt } from './aiEnrichment';

